import { useState, useRef } from 'react';

const FONT = "'DM Sans', sans-serif";

export type CupomAplicado = { codigo: string; desconto: number; valor_final: number };

type Props = {
  slug: string;
  selectionString?: string;
  plan?: 'musica' | 'video';
  applied: CupomAplicado | null;
  onApply: (cupom: CupomAplicado | null) => void;
  disabled?: boolean;
  initialCode?: string;
};

const MOTIVO_MSG: Record<string, string> = {
  cupom_nao_encontrado: 'Cupom não encontrado',
  cupom_expirado: 'Cupom expirado',
  cupom_esgotado: 'Cupom esgotado',
  cupom_primeira_compra: 'Cupom válido só para a primeira compra',
  erro_servidor: 'Erro ao validar cupom — tente de novo',
};

export default function CouponBox({ slug, selectionString = '', plan, applied, onApply, disabled, initialCode }: Props) {
  const [code, setCode] = useState(() => (initialCode || '').toUpperCase().slice(0, 24));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleApply = async () => {
    if (!code.trim() || loading) return;
    if (!selectionString && !plan) {
      setError('Escolha um pacote antes de aplicar');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const r = await fetch('/api/checkout?action=validar-cupom', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug, cupom: code.trim(), previas_selecionadas: selectionString || undefined, plano: plan }),
      });
      const d = await r.json();
      if (!r.ok || !d.valid) {
        setError(MOTIVO_MSG[d.motivo] || 'Cupom inválido');
        return;
      }
      onApply({ codigo: d.code, desconto: Number(d.desconto), valor_final: Number(d.valor_final) });
      setCode('');
    } catch {
      setError('Erro ao validar cupom — tente de novo');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = () => {
    setError(null);
    setCode('');
    onApply(null);
  };

  if (applied) {
    return (
      <div
        style={{
          padding: '12px 16px',
          borderRadius: 14,
          background: 'rgba(31,138,91,0.08)',
          border: '1.5px solid rgba(31,138,91,0.28)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          fontFamily: FONT,
          marginBottom: 14,
          animation: 'inspiraFadeIn 0.3s',
        }}
      >
        <div>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: '#1f8a5b', display: 'flex', alignItems: 'center', gap: 6 }}>
            <svg width="14" height="11" viewBox="0 0 14 11" fill="none">
              <path d="M1 5.5L5 9.5L13 1.5" stroke="#1f8a5b" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Cupom <span style={{ fontFamily: 'monospace', letterSpacing: '0.04em' }}>{applied.codigo}</span> aplicado
          </div>
          <div style={{ fontSize: 12, color: '#1f8a5b', opacity: 0.85, marginTop: 2 }}>
            Desconto de R$ {applied.desconto.toFixed(2).replace('.', ',')}
          </div>
        </div>
        <button
          type="button"
          onClick={handleRemove}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#1f8a5b',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            textDecoration: 'underline',
            fontFamily: FONT,
            flexShrink: 0,
          }}
        >
          Remover
        </button>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 14, fontFamily: FONT }}>
      <label
        style={{
          display: 'block',
          fontSize: 11,
          fontWeight: 700,
          color: 'var(--text-soft)',
          textTransform: 'uppercase',
          letterSpacing: '0.16em',
          marginBottom: 8,
        }}
      >
        Cupom de desconto
      </label>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          ref={inputRef}
          type="text"
          value={code}
          onChange={(e) => {
            setCode(e.target.value.toUpperCase().slice(0, 24));
            if (error) setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleApply();
            }
          }}
          placeholder="DIGITE O CÓDIGO"
          disabled={disabled || loading}
          maxLength={24}
          style={{
            flex: 1,
            padding: '12px 14px',
            borderRadius: 12,
            border: error ? '1.5px solid #d64c2b' : '1.5px solid var(--border-soft)',
            fontSize: 13,
            fontFamily: FONT,
            fontWeight: 600,
            letterSpacing: '0.06em',
            background: 'var(--surface-white)',
            color: 'var(--text-strong)',
            outline: 'none',
            transition: 'border-color 0.2s',
          }}
          onFocus={(e) => {
            if (!error) e.currentTarget.style.borderColor = 'var(--gold)';
          }}
          onBlur={(e) => {
            if (!error) e.currentTarget.style.borderColor = 'var(--border-soft)';
          }}
        />
        <button
          type="button"
          onClick={handleApply}
          disabled={!code.trim() || loading || disabled}
          style={{
            padding: '12px 20px',
            borderRadius: 12,
            background: code.trim() && !loading && !disabled ? 'var(--gold)' : 'var(--border-soft)',
            color: '#fff',
            border: 'none',
            fontSize: 13,
            fontWeight: 700,
            cursor: code.trim() && !loading && !disabled ? 'pointer' : 'default',
            fontFamily: FONT,
            transition: 'background 0.2s',
            minWidth: 80,
            letterSpacing: '0.02em',
          }}
        >
          {loading ? '…' : 'Aplicar'}
        </button>
      </div>
      {error && (
        <div style={{ marginTop: 8, fontSize: 12.5, color: '#9F2A1F', fontFamily: FONT }}>
          {error}
        </div>
      )}
    </div>
  );
}
